<template>
  <div class="irp-data-source-cell">
    <template v-if="firstCategory">
      <span class="irp-data-source-cell__source">
        <FontAwesomeIcon
          :icon="firstCategory.icon"
          class="irp-data-source-cell__icon"
        />
        <span class="irp-data-source-cell__label">
          {{ firstCategory.label }}
        </span>
      </span>
      <BittsPopover
        v-if="overflowCategories.length"
        trigger="hover"
        placement="bottomLeft"
        :mount-to-body="true"
      >
        <span class="irp-data-source-cell__overflow">
          +{{ overflowCategories.length }}
        </span>
        <template #content>
          <div class="irp-data-source-cell__list">
            <span
              v-for="category in overflowCategories"
              :key="category.label"
              class="irp-data-source-cell__source"
            >
              <FontAwesomeIcon
                :icon="category.icon"
                class="irp-data-source-cell__icon"
              />
              <span class="irp-data-source-cell__label">
                {{ category.label }}
              </span>
            </span>
          </div>
        </template>
      </BittsPopover>
    </template>
    <span v-else class="irp-data-source-cell__empty">&mdash;</span>
  </div>
</template>

<script setup lang="ts">
import { BittsPopover } from '@crossbeam/bitts';
import type { BittsIcon } from '@crossbeam/bitts';

import type { ICellRendererParams } from '@ag-grid-community/core';
import { computed } from 'vue';

import {
  DATABRICKS_DATA_SOURCE_TYPE,
  EXTERNAL_FILE_DATA_SOURCE_TYPE,
  FILE_UPLOAD_DATA_SOURCE_TYPE,
  GOOGLE_SHEETS_DATA_SOURCE_TYPE,
  OPEN_DATA_PARTNER_DATA_SOURCE_TYPE,
  SNOWFLAKE_DATA_SOURCE_TYPE,
} from '@/constants/data_sources';
import type { RecordOverviewPartnerType } from '@/types/individual_record';

type DataSourceCategoryType = {
  label: 'CRM' | 'Data Warehouse' | 'File' | 'Open Data';
  icon: BittsIcon;
};

const { params } = defineProps<{
  params: ICellRendererParams<RecordOverviewPartnerType>;
}>();

const CRM_CATEGORY: DataSourceCategoryType = {
  label: 'CRM',
  icon: ['far', 'building'],
};

const DATA_WAREHOUSE_CATEGORY: DataSourceCategoryType = {
  label: 'Data Warehouse',
  icon: ['far', 'warehouse'],
};

const FILE_CATEGORY: DataSourceCategoryType = {
  label: 'File',
  icon: ['far', 'file-arrow-up'],
};

const OPEN_DATA_CATEGORY: DataSourceCategoryType = {
  label: 'Open Data',
  icon: ['far', 'cloud'],
};

const DATA_WAREHOUSE_TYPES = new Set([
  SNOWFLAKE_DATA_SOURCE_TYPE,
  DATABRICKS_DATA_SOURCE_TYPE,
]);

const FILE_TYPES = new Set([
  FILE_UPLOAD_DATA_SOURCE_TYPE,
  GOOGLE_SHEETS_DATA_SOURCE_TYPE,
  EXTERNAL_FILE_DATA_SOURCE_TYPE,
]);

function categoryFor(integrationType: string) {
  if (DATA_WAREHOUSE_TYPES.has(integrationType)) return DATA_WAREHOUSE_CATEGORY;
  if (FILE_TYPES.has(integrationType)) return FILE_CATEGORY;
  if (integrationType === OPEN_DATA_PARTNER_DATA_SOURCE_TYPE) {
    return OPEN_DATA_CATEGORY;
  }
  return CRM_CATEGORY;
}

const categories = computed(() => [
  ...new Set((params.data?.data_sources ?? []).map(categoryFor)),
]);

const firstCategory = computed(() => categories.value[0]);
const overflowCategories = computed(() => categories.value.slice(1));
</script>

<style lang="pcss">
@reference "../../../css/theme.css";
.irp-data-source-cell {
  @apply flex items-center gap-4 h-full w-full text-neutral-text;
}

.irp-data-source-cell__source {
  @apply flex items-center gap-8 min-w-0;
}

.irp-data-source-cell__icon {
  @apply h-16 w-16 shrink-0 text-neutral-accent;
}

.irp-data-source-cell__label {
  @apply truncate text-sm;
}

.irp-data-source-cell__overflow {
  @apply shrink-0 cursor-default text-sm text-neutral-text-weak;
}

.irp-data-source-cell__list {
  @apply flex flex-col items-start gap-4 p-8;
}

.irp-data-source-cell__empty {
  @apply text-neutral-text-weak;
}
</style>
